import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client";
import {
  AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY,
  ENABLE_AMPLITUDE,
} from "core/constants/environment";

export interface ExperimentUserProperties {
  planType?: string;
  state?: string;
  carrierId?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface InitExperimentClientParams {
  userId: string;
  userProperties?: ExperimentUserProperties;
}

function filterDefinedProperties(
  userProperties: ExperimentUserProperties | undefined
): { [key: string]: string | number | boolean } {
  return Object.fromEntries(
    Object.entries(userProperties ?? {}).filter(
      (entry): entry is [string, string | number | boolean] =>
        entry[1] !== undefined
    )
  );
}

function createExperimentClient() {
  let instance: ExperimentClient | undefined;
  let startPromise: Promise<void> | undefined;
  const pendingResolvers: Array<() => void> = [];

  const isEnabled = () =>
    ENABLE_AMPLITUDE && Boolean(AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY);

  const drainResolvers = () => {
    pendingResolvers.forEach((resolve) => resolve());
    pendingResolvers.length = 0;
  };

  return {
    /**
     * Initializes the Amplitude Experiment client with the authenticated user's context.
     * Called from useAmplitudeExperiment once `user.id` becomes available.
     *
     * - No-op when Amplitude is disabled or deployment key is missing.
     * - No-op on second call (singleton guard via startPromise). Use updateUser() to refresh
     *   targeting properties after init.
     * - Always drains pendingResolvers on completion or error so ensureStarted() callers never hang.
     *
     * Failure modes:
     * - Sync errors from Experiment.initialize() reject the returned promise (caller can .catch).
     * - Async errors from instance.start() are caught internally; ensureStarted() resolves anyway
     *   so feature-flag reads degrade to enabled=false instead of hanging.
     */
    init: async (params: InitExperimentClientParams): Promise<void> => {
      if (!isEnabled()) {
        drainResolvers();
        return;
      }
      if (startPromise) return;

      instance = Experiment.initialize(AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY, {
        automaticExposureTracking: false,
      });

      startPromise = instance
        .start({
          user_id: params.userId,
          user_properties: filterDefinedProperties(params.userProperties),
        })
        .then(() => {
          drainResolvers();
        })
        .catch(() => {
          drainResolvers();
        });
    },

    /**
     * Refreshes user_properties on an already-initialized client and re-fetches variants.
     *
     * Use case: in this app, `application` loads after `user` (separate fetch via useAuth),
     * so the first call to init() typically passes undefined planType/state/carrierId.
     * Once the application loads, call updateUser() so targeting rules see the real values.
     *
     * - No-op when client has not been initialized yet (init() will pick up the latest props).
     * - No-op when Amplitude is disabled.
     * - Fire-and-forget: fetch errors are swallowed (existing variants remain in memory).
     */
    updateUser: (params: InitExperimentClientParams): void => {
      if (!isEnabled() || !instance) return;
      const user = {
        user_id: params.userId,
        user_properties: filterDefinedProperties(params.userProperties),
      };
      instance.setUser(user);
      instance.fetch(user).catch(() => {
        /* Non-fatal: prior variants remain usable */
      });
    },

    /**
     * Returns a promise that resolves when the Amplitude client is ready to serve variants.
     *
     * - init() already resolved → returns startPromise (already fulfilled)
     * - init() in flight → returns startPromise (awaits completion)
     * - init() not yet called → deferred promise pushed to pendingResolvers[], resolved by init()
     * - Amplitude disabled → resolves immediately
     */
    ensureStarted: (): Promise<void> => {
      if (!isEnabled()) return Promise.resolve();
      if (startPromise) return startPromise;
      return new Promise<void>((resolve) => {
        pendingResolvers.push(resolve);
      });
    },

    getInstance: (): ExperimentClient | undefined => instance,
  };
}

export const experimentClient = createExperimentClient();
